import { styled } from "@linaria/react";
import classNames from "classnames";
import React from "react";
import { colors } from "../Theme";
import { AlertLine, ErrorWarningLine, InformationLine } from "../IconV2/react";
import Text from "../Text";
import { css } from "@linaria/core";
import { fonts } from "..";

// Constants
const INFO = "info";
const WARNING = "warning";
const ERROR = "error";
const SIZE_S = "s";
const SIZE_M = "m";

// Types
interface P {
  status: "INFO" | "WARNING" | "ERROR";
  text: React.ReactNode;
  size?: "s" | "m";
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
}

const Icon = ({ status, size }: Pick<P, "status" | "size">) => {
  switch (status) {
    case "INFO":
      return <InformationLine size={size} />;
    case "WARNING":
      return <AlertLine size={size} />;
    case "ERROR":
      return <ErrorWarningLine size={size} />;
    default:
      return null;
  }
};

const AdditionnalMessage = ({ size = "m", status, text, className }: P) => (
  <Wrapper
    className={classNames(
      {
        [INFO]: status === "INFO",
        [WARNING]: status === "WARNING",
        [ERROR]: status === "ERROR",
      },
      className,
    )}
  >
    <IconWrapper>
      <Icon status={status} size={size} />
    </IconWrapper>
    <Text
      type="body1"
      className={classNames(overflow, {
        [SIZE_M]: size === "m",
        [SIZE_S]: size === "s",
      })}
    >
      {text}
    </Text>
  </Wrapper>
);

export default React.memo(AdditionnalMessage);

//#region CSS
// Styled components
const Wrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;

  &&&.${INFO} {
    color: ${colors.darkNitrogen600};
  }
  &&&.${WARNING} {
    color: ${colors.phosphorus600};
  }
  &&&.${ERROR} {
    color: ${colors.oxygen400};
  }
`;
const IconWrapper = styled.div`
  display: flex;
`; //Note: Prevent the browser from applying default sizing rules to the SVG element
const overflow = css`
  overflow: auto;

  &.${SIZE_S} {
    font: ${fonts.body2};
  }
  &.${SIZE_M} {
    font: ${fonts.body1};
  }
`;
//#endregion CSS
